import { useIsMobile } from '@src/hooks/useIsMobile';
import { EButtonIntents } from '@src/typings';
import { UploadFile as AntUploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import React, { useState } from 'react';
import { FaPlus } from 'react-icons/fa6';
import { GrUpdate } from 'react-icons/gr';
import { HiTrash } from 'react-icons/hi2';
import { Button } from '../Button';
import { withTheme } from '../ThemeProvider';
import { StyledDragger, StyledFormItem, StyledUploadFileIcon } from './styles';
import { UploadFileProps } from './types';

const UploadFileComponent: React.FC<UploadFileProps> = ({
    error,
    templateUrl,
    label,
    errorClassName,
    helperText,
    required,
    className,
    help,
    name,
    title,
    uploadBtn,
    validateStatus,
    rules,
    showRemoveIcon = true,
    showEditIcon = false,
    disabled,
    loading,
    formItemClassName,
    onEdit,
    onRemove,
    callBack,
    ...props
}) => {
    const [fileList, setFileList] = useState<AntUploadFile[]>([]);

    const isMobile = useIsMobile();

    const handleChange = (info: UploadChangeParam) => {
        if (props.multiple) {
            setFileList(info.fileList);
            callBack?.(info);
        } else {
            const latestFile = info.fileList[info.fileList.length - 1];
            const newFileList = latestFile ? [latestFile] : [];
            setFileList(newFileList);
            callBack?.({ ...info, fileList: newFileList });
        }
    };

    const handleRemove = (
        file: AntUploadFile,
        e?: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e?.stopPropagation();
        const newList = fileList.filter((f) => f.uid !== file.uid);
        setFileList(newList);
        onRemove?.(file);
        return true; // ✅ let AntD complete its removal
    };
    return (
        <StyledFormItem
            layout="vertical"
            name={name}
            label={label}
            required={required}
            className={formItemClassName}
            validateStatus={error ? 'error' : validateStatus}
            help={help}
            rules={rules}
        >
            <StyledDragger
                className={className}
                customRequest={props.customRequest}
                onChange={handleChange}
                onRemove={handleRemove}
                fileList={fileList}
                disabled={disabled || loading}
                showUploadList={false}
                {...props}
            >
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '16px',
                    }}
                >
                    {fileList.length === 0 &&
                        title &&
                        (typeof title === 'string' ? (
                            <h2 style={{ fontWeight: 500, fontSize: '20px' }}>
                                {title}
                            </h2>
                        ) : (
                            title
                        ))}
                    {fileList.length === 0 && uploadBtn && (
                        <Button
                            size={isMobile ? 'sm' : 'md'}
                            intent={EButtonIntents.Linear}
                            StartAdornment={FaPlus}
                            disabled={disabled || loading}
                        >
                            {uploadBtn}
                        </Button>
                    )}
                </div>

                {/* 🔽 Render file list inside the dragger box */}
                {fileList.map((file) => (
                    <div
                        key={file.uid}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '4px 8px',
                        }}
                    >
                        <span
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontWeight: 500,
                                fontSize: '16px',
                            }}
                        >
                            {file.name}
                        </span>
                        <div
                            style={{
                                display: 'flex',
                                gap: '8px',
                                alignItems: 'center',
                            }}
                        >
                            {showEditIcon && (
                                <StyledUploadFileIcon
                                    type="button"
                                    onClick={() => onEdit?.(file)}
                                >
                                    <GrUpdate size={12} />
                                </StyledUploadFileIcon>
                            )}
                            {showRemoveIcon && (
                                <StyledUploadFileIcon
                                    type="button"
                                    onClick={(e) => handleRemove(file, e)}
                                >
                                    <HiTrash size={14} />
                                </StyledUploadFileIcon>
                            )}
                        </div>
                    </div>
                ))}

                {fileList.length === 0 && (
                    <p
                        style={{
                            fontWeight: 'normal',
                            color:
                                props.theme?.extend.colors
                                    .fileUploadHelperText ||
                                'rgba(0, 0, 0, 0.48)',
                            fontSize: '16px',
                            textAlign: 'start',
                            marginTop: '8px',
                        }}
                    >
                        {helperText ??
                            'Each number needs to include a country code for acceptance. Accepted formats: .XLS, .XLSX, .CSV, .TXT.'}
                    </p>
                )}

                {templateUrl && (
                    <a
                        href={templateUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            color:
                                props.theme?.extend.colors.fileUploadLinkText ||
                                '#007BFF',
                            textDecoration: 'underline',
                            marginTop: '8px',
                            display: 'block',
                        }}
                    >
                        Download template
                    </a>
                )}
            </StyledDragger>
        </StyledFormItem>
    );
};

export const UploadFile = withTheme(UploadFileComponent);
